import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  calculateCotationPrice,
  getExpeditionPriceParameters,
} from "@/../../shared/helpers/cotationPriceCalculation";
import { getTvaApplications } from "@/../../shared/helpers/tvaApplicability";
import { useGetCustomV1Bo } from "@/api/customs.api";
import { useGetGlobalParameters } from "@/api/globalParameters.api";
import { useGetCompanyBOV1 } from "@/api/company.api";
import { useGetWoodboxPriceByCompanyId } from "@/api/woodboxPrice.api";
import {
  GetCustomV1BoResponse,
  GetOneExpeditionBoResponseDto,
  GetWoodboxPriceResponse,
} from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";

export type PriceChangeReason = "transport" | "taxes" | "insurance" | "woodBoxes";

export type PriceChange = {
  reason: PriceChangeReason;
  message: string;
  updatedAt?: Date;
};

export type UseQuotationPriceUpdateResult = {
  isReady: boolean;
  hasPriceChanged: boolean;
  currentTotal: number;
  newTotal: number;
  changes: PriceChange[];
};

const EMPTY_RESULT: UseQuotationPriceUpdateResult = {
  isReady: false,
  hasPriceChanged: false,
  currentTotal: 0,
  newTotal: 0,
  changes: [],
};

const pickWoodboxPrice = (
  custom: GetCustomV1BoResponse | undefined,
  woodboxPriceCompany: GetWoodboxPriceResponse | undefined,
  fallback: GetWoodboxPriceResponse | undefined,
): GetWoodboxPriceResponse => {
  const relevant =
    woodboxPriceCompany && woodboxPriceCompany.length > 0 ? woodboxPriceCompany : fallback || [];
  if (!custom || relevant.length === 0) return [];
  if (custom.label === "FRANCE") return relevant.filter((e) => e.region === "FRANCE");
  if (custom.region === "EUROPEANUNION")
    return relevant.filter((e) => e.region === "EUROPEANUNION");
  if (custom.region === "EUROPE") return relevant.filter((e) => e.region === "EUROPE");
  return relevant.filter((e) => e.region === "OTHER");
};

const pickWineValueTaxRange = (
  ranges: { from: number; to: number; value: number }[] | undefined,
  totalWineValue: number,
): number => {
  const sorted = [...(ranges || [])].sort((a, b) => a.from - b.from);
  if (sorted.length === 0) return 0;
  if (totalWineValue === 0) return sorted[0]?.value || 0;
  return (
    sorted.find((range) => range.from < totalWineValue && range.to >= totalWineValue)?.value ??
    sorted[sorted.length - 1]?.value ??
    0
  );
};

export const useQuotationPriceUpdate = (
  expedition: GetOneExpeditionBoResponseDto,
): UseQuotationPriceUpdateResult => {
  const { t } = useTranslation("boExpeditionForm");
  const { control } = useFormContext<QuotationUpdateBoFormData>();

  const [deliveryContent, dapOption, express, insurance, quotationWoodBox] = useWatch({
    control,
    name: [
      "deliveryContent",
      "options.dapOption",
      "options.express",
      "options.insurance",
      "quotationWoodBox",
    ],
  });

  const { data: custom } = useGetCustomV1Bo(expedition.quotation?.customId || "");
  const { data: globalParameters } = useGetGlobalParameters();
  const { data: company } = useGetCompanyBOV1(expedition.companyId);
  const { data: woodboxPriceCompany } = useGetWoodboxPriceByCompanyId(expedition.companyId);

  return useMemo<UseQuotationPriceUpdateResult>(() => {
    if (!expedition.quotation?.expeditionPriceParameters) return EMPTY_RESULT;

    const { customTrimmed, insurancePercentage, woodBoxMembranesPriceHT, woodboxPrices } =
      getExpeditionPriceParameters(expedition.quotation.expeditionPriceParameters);

    const applicableWoodboxPrice = pickWoodboxPrice(
      custom,
      woodboxPriceCompany,
      globalParameters?.woodBoxesPrice,
    );

    const filteredTarifs =
      custom?.customTarifs?.filter(
        (tarif) => tarif.id === company?.tarif.id || tarif.isExpress === true,
      ) || [];

    const products = (deliveryContent || []).map((product) => ({
      volume: product.volume,
      bottleQuantity: product.numberOfBottles,
      value: product.unitPrice * product.numberOfBottles,
    }));

    const woodBoxes = (quotationWoodBox || []).map((woodBox) => ({
      boxQuantity: woodBox.boxQuantity,
      bottleVolume: woodBox.bottleVolume,
      bottleQuantity: woodBox.bottleQuantity,
    }));

    const formData = {
      products,
      dapOption: dapOption || false,
      express: express || false,
      insurance: insurance || false,
      woodBoxesActivated: woodBoxes.length > 0,
      woodBoxes,
    };

    const billingEntityCountry =
      company?.billingEntities?.find((e) => e.isDefault)?.country ?? "FR";
    const isDestinationEU = custom?.label === "FRANCE" || custom?.region === "EUROPEANUNION";
    const tvaApplications = getTvaApplications(billingEntityCountry, isDestinationEU);

    const currentPrice = calculateCotationPrice(
      formData,
      customTrimmed,
      woodBoxMembranesPriceHT || 0,
      insurancePercentage || 0,
      woodboxPrices,
      tvaApplications,
    );

    const newPrice = calculateCotationPrice(
      formData,
      custom ? { ...custom, customTarifs: filteredTarifs } : customTrimmed,
      globalParameters?.woodBoxMembranesPriceHT || 0,
      company?.optionInsuranceActive
        ? company?.insurancePercentage || 0
        : globalParameters?.insurancePercentage || 0,
      applicableWoodboxPrice,
      tvaApplications,
    );

    const hasInsuranceChanged = currentPrice.insurance !== newPrice.insurance;
    const hasWoodBoxesChanged = currentPrice.woodBoxes !== newPrice.woodBoxes;
    const hasTransportChanged =
      currentPrice.transport?.transportHt !== newPrice.transport?.transportHt;
    const hasTaxesValueChanged = currentPrice.taxes !== newPrice.taxes;

    const totalWineValue = (deliveryContent || []).reduce(
      (acc, product) => acc + product.unitPrice * product.numberOfBottles,
      0,
    );

    const taxParametersChanged =
      customTrimmed?.taxPerShipment !== custom?.taxPerShipment ||
      customTrimmed?.wineValueTaxPercentage !== custom?.wineValueTaxPercentage ||
      customTrimmed?.taxPerLiter !== custom?.taxPerLiter ||
      customTrimmed?.taxPerBottle !== custom?.taxPerBottle ||
      customTrimmed?.transportPlusWineValueTaxPercentage !==
        custom?.transportPlusWineValueTaxPercentage ||
      customTrimmed?.transportPlusWinePlusInsuranceTaxPercentage !==
        custom?.transportPlusWinePlusInsuranceTaxPercentage ||
      pickWineValueTaxRange(customTrimmed?.customWineValueTaxRanges, totalWineValue) !==
        pickWineValueTaxRange(custom?.customWineValueTaxRanges, totalWineValue);

    const hasTaxesChanged = hasTaxesValueChanged && taxParametersChanged;

    const insuranceUpdatedAt = company?.optionInsuranceActive
      ? company?.updatedAt
      : globalParameters?.updatedAt;
    const woodBoxesUpdatedAt =
      woodboxPriceCompany && woodboxPriceCompany.length > 0
        ? woodboxPriceCompany[0]?.updatedAt
        : globalParameters?.updatedAt;

    const changes: PriceChange[] = [];
    if (hasInsuranceChanged) {
      changes.push({
        reason: "insurance",
        message: t("priceUpdate.insuranceChanged"),
        updatedAt: insuranceUpdatedAt ? new Date(insuranceUpdatedAt) : undefined,
      });
    }
    if (hasWoodBoxesChanged) {
      changes.push({
        reason: "woodBoxes",
        message: t("priceUpdate.woodBoxesChanged"),
        updatedAt: woodBoxesUpdatedAt ? new Date(woodBoxesUpdatedAt) : undefined,
      });
    }
    if (hasTaxesChanged) {
      changes.push({
        reason: "taxes",
        message: t("priceUpdate.taxesChanged"),
        updatedAt: custom?.updatedAt ? new Date(custom.updatedAt) : undefined,
      });
    }
    if (hasTransportChanged) {
      changes.push({
        reason: "transport",
        message: t("priceUpdate.transportChanged"),
        updatedAt: custom?.updatedAt ? new Date(custom.updatedAt) : undefined,
      });
    }

    return {
      isReady: true,
      hasPriceChanged: currentPrice.totalPrice !== newPrice.totalPrice,
      currentTotal: currentPrice.totalPrice,
      newTotal: newPrice.totalPrice,
      changes,
    };
  }, [
    expedition.quotation?.expeditionPriceParameters,
    deliveryContent,
    dapOption,
    express,
    insurance,
    quotationWoodBox,
    custom,
    company,
    globalParameters,
    woodboxPriceCompany,
    t,
  ]);
};
